import { DragEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { Logo } from '../api/types';
import { useAuth } from '../contexts/AuthContext';
import { LogoPreview } from '../components/LogoPreview';
import { LogoEditorModal, LogoCrop } from '../components/LogoEditorModal';

const DEFAULT_CROP: LogoCrop = { cropX: 0, cropY: 0, cropWidth: 1, cropHeight: 1, zoom: 1 };

export function LogosPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [logos, setLogos] = useState<Logo[]>([]);
  const [search, setSearch] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ svgContent: string; companyName: string } | null>(null);
  const [editing, setEditing] = useState<Logo | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canDelete = user?.globalRole === 'ADMIN' || user?.globalRole === 'SUPER_USER';

  const load = (q?: string) => {
    api
      .get<Logo[]>(`/logos${q ? `?search=${encodeURIComponent(q)}` : ''}`)
      .then(setLogos)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  };

  useEffect(() => {
    const handle = setTimeout(() => load(search), 250);
    return () => clearTimeout(handle);
  }, [search]);

  const readSvgFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.svg') && file.type !== 'image/svg+xml') {
      setError(t('logos.invalidFile'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setError(null);
      setPending({ svgContent: String(reader.result), companyName: file.name.replace(/\.svg$/i, '') });
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) readSvgFile(file);
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) readSvgFile(file);
    e.target.value = '';
  };

  const handleSaveNew = async (crop: LogoCrop) => {
    if (!pending) return;
    try {
      await api.post('/logos', { companyName: pending.companyName, svgContent: pending.svgContent, ...crop });
      setPending(null);
      load(search);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const handleSaveEdit = async (crop: LogoCrop) => {
    if (!editing) return;
    try {
      await api.patch(`/logos/${editing.id}`, crop);
      setEditing(null);
      load(search);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('logos.deleteConfirm'))) return;
    await api.delete(`/logos/${id}`);
    load(search);
  };

  return (
    <div>
      <h2>{t('logos.title')}</h2>

      <input
        type="search"
        placeholder={t('logos.search') ?? ''}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: '100%', maxWidth: 320, marginBottom: 16, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}
      />

      {error && <p className="error-text">{error}</p>}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className="card"
        style={{
          marginBottom: 24,
          borderStyle: 'dashed',
          borderColor: dragOver ? 'var(--color-primary)' : 'var(--color-border)',
          textAlign: 'center',
          padding: 32,
          cursor: 'pointer',
          color: 'var(--color-text-muted)',
        }}
      >
        {t('logos.dropzone')}
        <input ref={fileInputRef} type="file" accept=".svg,image/svg+xml" onChange={handleFilePick} style={{ display: 'none' }} />
      </div>

      <div className="grid">
        {logos.map((logo) => (
          <div key={logo.id} className="item-card" style={{ cursor: 'default', alignItems: 'center' }}>
            <LogoPreview svgContent={logo.svgContent} cropX={logo.cropX} cropY={logo.cropY} zoom={logo.zoom} size={100} />
            <strong style={{ marginTop: 8 }}>{logo.companyName}</strong>
            <span className="badge">
              {logo.uploadedBy.firstName} {logo.uploadedBy.lastName}
            </span>
            <div className="toolbar" style={{ marginTop: 6 }}>
              <button className="btn" style={{ padding: '2px 8px' }} onClick={() => setEditing(logo)}>
                {t('logos.edit')}
              </button>
              {canDelete && (
                <button className="btn btn-danger" style={{ padding: '2px 8px' }} onClick={() => handleDelete(logo.id)}>
                  {t('folders.delete')}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {pending && (
        <LogoEditorModal
          svgContent={pending.svgContent}
          initial={DEFAULT_CROP}
          companyName={pending.companyName}
          onCompanyNameChange={(v) => setPending({ ...pending, companyName: v })}
          onCancel={() => setPending(null)}
          onSave={handleSaveNew}
        />
      )}

      {editing && (
        <LogoEditorModal
          svgContent={editing.svgContent}
          initial={{ cropX: editing.cropX, cropY: editing.cropY, cropWidth: editing.cropWidth, cropHeight: editing.cropHeight, zoom: editing.zoom }}
          onCancel={() => setEditing(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}
